import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

/**
 * Reveal — anima o conteúdo (fade + slide up) quando ele entra na viewport.
 *
 * Usa IntersectionObserver e respeita `prefers-reduced-motion`: se o usuário
 * pedir menos movimento, o conteúdo já aparece visível, sem animação.
 *
 * @param {number} delay - atraso da animação em ms (para efeito escalonado).
 * @param {string} as - tag/elemento de saída (default div).
 */
const Wrap = styled.div`
  opacity: ${(p) => (p.$shown ? 1 : 0)};
  transform: ${(p) => (p.$shown ? "none" : "translateY(22px)")};
  transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: ${(p) => p.$delay || 0}ms;
  will-change: opacity, transform;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

export default function Reveal({ children, delay = 0, as, className, id }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Wrap ref={ref} as={as} className={className} id={id} $shown={shown} $delay={delay}>
      {children}
    </Wrap>
  );
}
